import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, FileText, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { createPIP } from "@/services/performanceService";

const MIN_PIP_REASON_LENGTH = 15;

const todayStr = () => new Date().toISOString().split("T")[0];

const buildInitialForm = () => ({
  employeeId: "",
  reason: "",
  startDate: todayStr(),
  endDate: "",
  templateId: "",
});

// Auto-generate review milestones based on the PIP duration.
const generateMilestones = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return [];

  const milestones = [];
  const addDate = (d, days) => {
    const nd = new Date(d);
    nd.setDate(nd.getDate() + days);
    return nd.toISOString().split("T")[0];
  };

  if (diffDays <= 14) {
    milestones.push({ id: "m1", label: "Mid-Point Check-in", date: addDate(start, Math.round(diffDays / 2)), completed: false, status: "Pending", notes: "" });
    milestones.push({ id: "m2", label: "Final Review", date: endDate, completed: false, status: "Pending", notes: "" });
  } else if (diffDays <= 30) {
    milestones.push({ id: "m1", label: "Week 1 Check-in", date: addDate(start, 7), completed: false, status: "Pending", notes: "" });
    milestones.push({ id: "m2", label: "Mid-Point Review", date: addDate(start, Math.round(diffDays / 2)), completed: false, status: "Pending", notes: "" });
    milestones.push({ id: "m3", label: "Final Review", date: endDate, completed: false, status: "Pending", notes: "" });
  } else if (diffDays <= 60) {
    milestones.push({ id: "m1", label: "Week 1 Check-in", date: addDate(start, 7), completed: false, status: "Pending", notes: "" });
    milestones.push({ id: "m2", label: "Week 2 Check-in", date: addDate(start, 14), completed: false, status: "Pending", notes: "" });
    milestones.push({ id: "m3", label: "Mid-Point Review", date: addDate(start, Math.round(diffDays / 2)), completed: false, status: "Pending", notes: "" });
    milestones.push({ id: "m4", label: "Final Review", date: endDate, completed: false, status: "Pending", notes: "" });
  } else {
    const interval = Math.round(diffDays / 5);
    for (let i = 1; i <= 4; i++) {
      milestones.push({ id: `m${i}`, label: `Review ${i}`, date: addDate(start, interval * i), completed: false, status: "Pending", notes: "" });
    }
    milestones.push({ id: "m5", label: "Final Review", date: endDate, completed: false, status: "Pending", notes: "" });
  }
  return milestones;
};

/**
 * Self-contained "Assign PIP" dialog. Owns its own form/milestone state so that
 * typing in the form only re-renders this component, not the whole page.
 *
 * Props:
 *  - employeeList: [{ id, name }]
 *  - templates: [{ _id|id, name, type }]
 *  - onCreated: () => void  — called after a PIP is successfully created (e.g. to reload data)
 */
export default function PIPFormDialog({
  employeeList = [],
  templates = [],
  onCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger = false,
}) {
  const navigate = useNavigate();
  const { can } = usePermission();
  // Memoize so typing in the form doesn't re-invoke can() on every keystroke.
  const { canCreate } = useMemo(() => createCanHelpers(can), [can]);
  const canCreatePIP = useMemo(() => canCreate("performance_management"), [canCreate]);

  // Controlled when a parent passes `open`/`onOpenChange` (e.g. driven from the
  // Performance "Actions" dropdown); otherwise self-managed with its own trigger.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    controlledOnOpenChange?.(next);
  };
  const [pipForm, setPipForm] = useState(buildInitialForm);
  const [milestones, setMilestones] = useState([]);
  const [pipCreating, setPipCreating] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");

  const normalizedPipReason = pipForm.reason.trim();

  // Regenerate milestones whenever the date range changes.
  useEffect(() => {
    if (pipForm.startDate && pipForm.endDate) {
      setMilestones(generateMilestones(pipForm.startDate, pipForm.endDate));
    }
  }, [pipForm.startDate, pipForm.endDate]);

  const resetForm = () => {
    setPipForm(buildInitialForm());
    setMilestones([]);
    setEmployeeSearchQuery("");
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) resetForm();
  };

  const handleCreate = async () => {
    if (!canCreate("performance_management", true)) return;

    if (!pipForm.employeeId || !normalizedPipReason || !pipForm.templateId || !pipForm.startDate || !pipForm.endDate) {
      toast.error("Please fill in all required fields");
      return;
    }

    setPipCreating(true);
    try {
      const response = await createPIP(
        pipForm.employeeId,
        pipForm.templateId,
        normalizedPipReason,
        pipForm.startDate,
        pipForm.endDate,
        milestones.length > 0 ? milestones : generateMilestones(pipForm.startDate, pipForm.endDate)
      );

      toast.success(response.message || "PIP created successfully!");
      setOpen(false);
      resetForm();
      onCreated?.();
    } catch (error) {
      console.error("Error creating PIP:", error);
      toast.error(error.response?.data?.message || "Failed to create PIP");
    } finally {
      setPipCreating(false);
    }
  };

  const filteredEmployees = employeeList.filter(
    (emp) =>
      emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
      emp.id.toLowerCase().includes(employeeSearchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="default"
            size="sm"
            className={`bg-primary hover:bg-primary/90 ${
              !canCreatePIP ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={!canCreatePIP}
            data-testid="assign-pip-btn"
          >
            <AlertCircle className="mr-1.5 h-4 w-4" /> Assign PIP
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-primary flex items-center gap-2">
            <AlertCircle className="h-5 w-5" /> Assign Performance Improvement Plan
          </DialogTitle>
          <DialogDescription>
            Create a structured PIP with scheduled review milestones.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Select Employee *</Label>
              <Select
                value={pipForm.employeeId}
                onValueChange={(value) => {
                  setPipForm((prev) => ({ ...prev, employeeId: value }));
                  setEmployeeSearchQuery("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2" onClick={(e) => e.stopPropagation()}>
                    <Input
                      placeholder="Search employee..."
                      value={employeeSearchQuery}
                      onChange={(e) => {
                        e.stopPropagation();
                        setEmployeeSearchQuery(e.target.value);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="h-8"
                    />
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {filteredEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                    {filteredEmployees.length === 0 && (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No employee found.
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>PIP Template *</Label>
              <Select
                value={pipForm.templateId}
                onValueChange={(value) =>
                  setPipForm((prev) => ({ ...prev, templateId: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template..." />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const filteredTemplates = Array.isArray(templates)
                      ? templates.filter((t) => t.type === "PIP")
                      : [];

                    if (filteredTemplates.length === 0) {
                      return (
                        <div className="py-6 px-4 text-center">
                          <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-2">
                            No PIP templates available
                          </p>
                          <Button
                            variant="link"
                            className="text-xs h-auto p-0"
                            onClick={() => navigate("/template-builder")}
                          >
                            Create template
                          </Button>
                        </div>
                      );
                    }

                    return filteredTemplates.map((t) => (
                      <SelectItem key={t._id || t.id} value={t._id || t.id}>
                        {t.name}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Reason for PIP *</Label>
            <Textarea
              className="w-full min-h-[60px] p-2 rounded-md border bg-transparent text-sm resize-none"
              value={pipForm.reason}
              onChange={(e) =>
                setPipForm((prev) => ({ ...prev, reason: e.target.value }))
              }
              placeholder="Describe the performance concerns..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={pipForm.startDate}
                onChange={(e) =>
                  setPipForm((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>End Date *</Label>
              <Input
                type="date"
                value={pipForm.endDate}
                onChange={(e) =>
                  setPipForm((prev) => ({ ...prev, endDate: e.target.value }))
                }
                min={pipForm.startDate}
              />
            </div>
          </div>
          {pipForm.startDate && pipForm.endDate && (
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="text-sm border-primary/30 text-primary"
              >
                {Math.round(
                  (new Date(pipForm.endDate) - new Date(pipForm.startDate)) /
                    (1000 * 60 * 60 * 24)
                )}{" "}
                day PIP period
              </Badge>
            </div>
          )}
          {pipForm.startDate && pipForm.endDate && (
            <div className="space-y-2">
              <Label>Review Milestones</Label>
              <div className="space-y-2 max-h-[120px] overflow-y-auto pr-1">
                {milestones.map((ms, idx) => (
                  <div key={ms.id} className="flex items-center gap-2 m-0.5">
                    <Input
                      className="flex-1"
                      value={ms.label}
                      onChange={(e) => {
                        const updated = [...milestones];
                        updated[idx] = {
                          ...updated[idx],
                          label: e.target.value,
                        };
                        setMilestones(updated);
                      }}
                    />
                    <Input
                      type="date"
                      className="h-8 text-sm w-36"
                      value={ms.date}
                      onChange={(e) => {
                        const updated = [...milestones];
                        updated[idx] = {
                          ...updated[idx],
                          date: e.target.value,
                        };
                        setMilestones(updated);
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-primary hover:bg-primary/10 shrink-0"
                      onClick={() =>
                        setMilestones(milestones.filter((_, i) => i !== idx))
                      }
                      disabled={milestones.length <= 1}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1"
                onClick={() => {
                  const addDate = (d, days) => {
                    const nd = new Date(d);
                    nd.setDate(nd.getDate() + days);
                    return nd.toISOString().split("T")[0];
                  };
                  setMilestones([
                    ...milestones,
                    {
                      id: "m" + Date.now(),
                      label: "New Milestone",
                      date: pipForm.endDate || addDate(pipForm.startDate, 7),
                      status: "Pending",
                      notes: "",
                    },
                  ]);
                }}
              >
                <Plus size={12} /> Add Milestone
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="default"
            onClick={handleCreate}
            disabled={
              pipCreating ||
              !pipForm.employeeId ||
              !pipForm.templateId ||
              !pipForm.startDate ||
              !pipForm.endDate ||
              !normalizedPipReason
            }
          >
            {pipCreating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            Assign PIP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
